/**
 * 남은 설정 단계 중 **지금 해야 하는 것 하나**를 고릅니다.
 *
 * 예전 배너는 무엇이 빠졌는지 보지 않고 늘 "아직 토스증권 계좌가 연결되지 않았어요" 를
 * 띄웠어요. 라우터 게이트가 배너를 가려 줘서 드러나지 않았을 뿐이고, 게이트를 없앤 뒤에는
 * 키가 등록되고 IP 까지 확인된 계좌에서도 그 문구가 그대로 보였습니다 — 헤더는
 * "토스증권 연결됨", 요약은 계좌번호를 보여주는데 배너만 연결이 안 됐다고 말하는 상태였고,
 * stage 에서 실제로 확인했습니다.
 *
 * 순서가 곧 의존성입니다: 키가 없으면 IP 를 확인할 수 없고, 연결이 안 되면 규칙을 돌릴 수
 * 없어요. 그래서 앞 단계가 안 끝났으면 뒤 단계를 먼저 안내하지 않습니다.
 */

import type { OnboardingProgress } from "@/types/api";

export interface OnboardingStep {
  /** `OnboardingProgress` 의 어느 플래그를 보는지 — 테스트와 디버깅에서 단계를 지목할 때 씁니다. */
  key: keyof OnboardingProgress;
  message: string;
  label: string;
  to: string;
}

export const ONBOARDING_STEPS: readonly OnboardingStep[] = [
  {
    key: "credential_registered",
    message: "토스증권 API 키를 등록하면 잔고와 보유 종목을 볼 수 있어요.",
    label: "키 등록하기",
    to: "/onboarding/api-key",
  },
  {
    key: "ip_registered_confirmed",
    message: "토스증권 앱에서 트랑슈 고정 IP를 허용 IP로 등록했는지 확인해 주세요.",
    label: "허용 IP 확인하기",
    to: "/onboarding/allow-ip",
  },
  {
    key: "first_strategy_created",
    message: "아직 만든 규칙이 없어요. 규칙을 만들면 백테스트로 먼저 검증해요.",
    label: "규칙 만들기",
    to: "/rules/new",
  },
  // `sor_confirmed` 는 단계로 두지 않습니다. SOR(투자자지시 거래소 통합)은 토스증권 앱에서
  // 켜는 설정이고 우리가 조회할 수단이 없어요 — 켰는지 물어볼 수는 있어도 확인할 수는 없고,
  // 그래서 이 플래그를 true 로 만드는 코드가 어디에도 없었습니다. 그 결과 배너는 설정을
  // 실제로 켠 뒤에도 영구히 남았고, 뒤이어 뜰 다른 안내를 계속 가렸어요.
  // SOR 안내가 필요한 자리는 두 곳에 그대로 있습니다: 키 등록 가이드(`routes/credentials.py`
  // 의 `ONBOARDING_STEPS` 마지막 줄)와, 실제로 주문이 거부됐을 때의 오류 문구
  // (`investor-exchange-not-integrated`). 후자는 추측이 아니라 토스가 알려준 사실입니다.
];

/** 남은 첫 단계. 전부 끝났으면 `null` — 호출자는 배너를 그리지 않습니다. */
export function nextOnboardingStep(onboarding: OnboardingProgress): OnboardingStep | null {
  return ONBOARDING_STEPS.find((step) => !onboarding[step.key]) ?? null;
}
